import { createContext, useContext, useEffect, useRef, useCallback, useState, useMemo } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";

const ClusterContext = createContext(null);

export const MapClusterProvider = ({ children, onClusterClick }) => {
  const map = useMap();
  const clusterer = useRef(null);
  const onClusterClickRef = useRef(onClusterClick);
  const markerDataMap = useRef(new Map());
  const [markers, setMarkers] = useState(new Map());

  useEffect(() => {
    onClusterClickRef.current = onClusterClick;
  }, [onClusterClick]);

  useEffect(() => {
    if (!map) return;

    if (!clusterer.current) {
      // const algorithm = new SuperClusterAlgorithm({
      //   maxZoom: 25,
      //   radius: 50
      // });

      clusterer.current = new MarkerClusterer({
        map,
        onClusterClick: (event, cluster) => {
          const itemsId = cluster.markers
            .map(marker => markerDataMap.current.get(marker))
            .filter(Boolean);

          if (onClusterClickRef.current) {
            onClusterClickRef.current(itemsId, cluster.marker);
          }
        }
      });
    }

    return () => {
      if (clusterer.current) {
        clusterer.current.clearMarkers();
        clusterer.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (clusterer.current) {
      clusterer.current.clearMarkers();
      clusterer.current.addMarkers(Array.from(markers.values()));
    }
  }, [markers]);

  const addMarker = useCallback((marker, item) => {
    if (marker) {
      markerDataMap.current.set(marker, item);
      setMarkers((prev) => {
        if (prev.has(marker)) return prev;
        const next = new Map(prev);
        next.set(marker, marker);
        return next;
      });
    }
  }, []);

  const removeMarker = useCallback((marker) => {
    if (marker) {
      markerDataMap.current.delete(marker);
      setMarkers((prev) => {
        if (!prev.has(marker)) return prev;
        const next = new Map(prev);
        next.delete(marker);
        return next;
      });
    }
  }, []);

  const contextValue = useMemo(() => ({
    addMarker,
    removeMarker
  }), [addMarker, removeMarker]);

  return (
    <ClusterContext.Provider value={contextValue}>
      {children}
    </ClusterContext.Provider>
  );
};

export const useMapCluster = () => useContext(ClusterContext);